import { clientsApi } from "#/api/clientsApi";
import { uploadApi } from "#/api/uploadApi";

import {
  showErrorNotification,
  showSuccessNotification,
} from "#/components/notifications";
import {
  SupportedAuthenticationMethod,
  type CreateClientPayload,
} from "#/types/client";
import { sanitize } from "#/utils/StringUtils";
import { logger } from "#/utils/logger";
import {
  Button,
  ColorInput,
  Drawer,
  Select,
  TextInput,
  type DrawerProps,
} from "@mantine/core";
import { useForm } from "@mantine/form";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePermission } from "#/hooks/usePermission";
import ImagePicker from "#/components/ui/ImagePicker";
import PermissionTooltip from "#/components/ui/PermissionTooltip";

export interface Props extends DrawerProps {
  onSuccess: (clientId: number) => void;
}

const CreateClientDrawer = ({ onSuccess, ...props }: Props) => {
  const { t } = useTranslation();
  const { canWrite } = usePermission();
  const [saving, setSaving] = useState<boolean>(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);

  const form = useForm<CreateClientPayload>({
    initialValues: {
      name: "",
      slug: "",
      color: "",
      supportedAuthenticationMethods: SupportedAuthenticationMethod.PASSWORD,
      address: "",
      contactEmail: "",
    },
    validate: {
      name: (value) => (value ? null : t("clients.validation.nameRequired")),
      slug: (value) => (value ? null : t("clients.validation.slugRequired")),
      color: (value) => (value ? null : t("clients.validation.colorRequired")),
      address: (value) => (value ? null : t("clients.validation.addressRequired")),
      contactEmail: (value) => {
        if (!value) {
          return t("clients.validation.contactEmailRequired");
        }
        return /^\S+@\S+$/.test(value) ? null : t("clients.validation.invalidEmail");
      },
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    if (!logoFile) {
      showErrorNotification({
        message: t("clients.validation.pleaseSelectLogo"),
      });
      return;
    }
    setSaving(true);
    try {
      const logoUrl = await uploadApi.upload(logoFile);
      const response = await clientsApi.create({
        ...values,
        logo: logoUrl,
      });
      setSaving(false);
      showSuccessNotification({
        message: t("clients.createdSuccess"),
      });
      form.reset();
      setLogoFile(null);
      onSuccess(response.data.id);
    } catch (error) {
      logger.error("CreateNewClientDrawer handleSubmit error:", error);
      setSaving(false);
    }
  };

  return (
    <Drawer
      {...props}
      position={"right"}
      title={t("clients.createNewClient")}
      closeOnClickOutside={false}
      onClose={() => {
        form.reset();
        props.onClose();
      }}
    >
      <form
        className="space-y-8 relative"
        onSubmit={form.onSubmit(handleSubmit)}
      >
        <div className="w-full grid grid-cols-1  gap-4">
          <TextInput
            label={t("clients.form.name")}
            withAsterisk
            placeholder={t("clients.form.namePlaceholder")}
            {...form.getInputProps("name")}
          />

          <TextInput
            label={t("clients.form.slug")}
            withAsterisk
            placeholder={t("clients.form.slugPlaceholder")}
            description={t("clients.form.slugDescription")}
            {...form.getInputProps("slug")}
          />

          <Select
            label={t("clients.form.authMethod")}
            placeholder={t("clients.form.authMethodPlaceholder")}
            searchable
            data={Object.values(SupportedAuthenticationMethod).map(
              (status) => ({
                label: sanitize(status),
                value: status,
              })
            )}
            value={form.values.supportedAuthenticationMethods}
            clearable
            onChange={(_value) => {
              form.setValues({
                ...form.values,
                supportedAuthenticationMethods:
                  _value as SupportedAuthenticationMethod,
              });
            }}
          />

          <TextInput
            label={t("clients.form.address")}
            withAsterisk
            placeholder={t("clients.form.addressPlaceholder")}
            {...form.getInputProps("address")}
          />

          <TextInput
            label={t("clients.form.contactEmail")}
            withAsterisk
            placeholder={t("clients.form.contactEmailPlaceholder")}
            {...form.getInputProps("contactEmail")}
          />

          <ColorInput
            label={t("clients.form.color")}
            withAsterisk
            placeholder={t("clients.form.colorPlaceholder")}
            {...form.getInputProps("color")}
          />

          <ImagePicker
            label={t("clients.form.logo")}
            value={null}
            onChange={setLogoFile}
            alt="Logo"
          />
        </div>

        <div className="w-full grid grid-cols-2 gap-4 sticky bottom-0 left-0 bg-white border-t border-t-gray-200 py-6">
          <Button
            color="black"
            variant="outline"
            type="reset"
            onClick={() => {
              form.reset();
            }}
            size="md"
          >
            {t("common.clear")}
          </Button>

          <PermissionTooltip disabled={!canWrite()}>
            <Button
              color="black"
              variant="filled"
              loading={saving}
              type="submit"
              size="md"
              disabled={!canWrite()}
              onClick={() => {
                void handleSubmit(form.values);
              }}
            >
              {t("common.save")}
            </Button>
          </PermissionTooltip>
        </div>
      </form>
    </Drawer>
  );
};

export default CreateClientDrawer;
